import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api } from '../../../services/api';
import { message } from 'antd';
import { ProcessTemplate, ProcessStage, StageOperation, Operation, GanttNode, TimeBlock } from '../types';
import { buildGanttNodes, generateTimeBlocks } from '../utils';

/**
 * 外部数据配置（用于批次模式）
 */
export interface ExternalGanttData {
    ganttNodes: GanttNode[];
    startDay: number;
    endDay: number;
    timeBlocks?: TimeBlock[];
    baseDate?: string; // ISO 日期字符串，用于显示实际日期
}

export interface UseGanttDataOptions {
    template: ProcessTemplate;
    externalData?: ExternalGanttData;
}

/**
 * useGanttData hook - 支持两种模式：
 * 1. 模板模式：从 API 加载数据（默认）
 * 2. 批次模式：使用外部提供的数据
 */
export const useGanttData = (options: UseGanttDataOptions | ProcessTemplate) => {
    // 兼容旧的调用方式
    const { template, externalData } = useMemo(() => {
        if ('template' in options) {
            return options as UseGanttDataOptions;
        }
        return { template: options as ProcessTemplate, externalData: undefined };
    }, [options]);

    const isExternalMode = !!externalData;

    const [stages, setStages] = useState<ProcessStage[]>([]);
    const [stageOperations, setStageOperations] = useState<{ [key: number]: StageOperation[] }>({});
    const [availableOperations, setAvailableOperations] = useState<Operation[]>([]);
    const [ganttNodes, setGanttNodes] = useState<GanttNode[]>([]);
    const [timeBlocks, setTimeBlocks] = useState<TimeBlock[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [personnelCurve, setPersonnelCurve] = useState<{ points: { hourIndex: number; requiredPeople: number }[]; peak?: { hourIndex: number; requiredPeople: number } | null }>({ points: [], peak: null });

    // 从 API 加载数据（模板模式）
    const fetchData = useCallback(async () => {
        if (isExternalMode) return; // 外部模式不从 API 加载

        const templateId = Number(template.id);
        if (!Number.isInteger(templateId) || templateId <= 0) {
            setStages([]);
            setStageOperations({});
            setGanttNodes([]);
            setTimeBlocks([]);
            setExpandedKeys([]);
            setPersonnelCurve({ points: [], peak: null });
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            // 获取阶段数据
            const stagesResponse = await api.get(`/process-stages/template/${templateId}`);
            const fetchedStages = stagesResponse.data;
            setStages(fetchedStages);

            // 获取可用操作
            const operationsResponse = await api.get(`/stage-operations/available`);
            setAvailableOperations(operationsResponse.data);

            // 并行获取每个阶段的操作（Promise.all，任一失败则整体 catch）
            const stageOpsMap: { [key: number]: StageOperation[] } = {};
            const stageOpsResults = await Promise.all(
                fetchedStages.map((stage: ProcessStage) =>
                    api.get(`/stage-operations/stage/${stage.id}`)
                )
            );
            fetchedStages.forEach((stage: ProcessStage, idx: number) => {
                stageOpsMap[stage.id] = stageOpsResults[idx].data;
            });
            setStageOperations(stageOpsMap);

            // 构建甘特图节点
            const nodes = buildGanttNodes(template, fetchedStages, stageOpsMap);
            setGanttNodes(nodes);

            // 生成时间块
            const blocks = generateTimeBlocks(nodes, fetchedStages);
            setTimeBlocks(blocks);

            // 默认只展开根节点（模版名称）
            const defaultExpandedKeys = [templateId.toString()];
            setExpandedKeys(defaultExpandedKeys);

            // 加载人员用量曲线
            try {
                const curveResponse = await api.get(`/process-templates/${templateId}/personnel-curve`);
                setPersonnelCurve({
                    points: curveResponse.data.points || [],
                    peak: curveResponse.data.peak || null
                });
            } catch (curveError) {
                console.error('Failed to load personnel curve:', curveError);
                setPersonnelCurve({ points: [], peak: null });
            }

        } catch (error) {
            const detail = (error as any)?.response?.data?.error || (error as Error)?.message || '加载模板数据失败';
            setError(detail);
            message.error('加载模板数据失败');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [template, isExternalMode]);

    // 追踪上次的节点ID集合，用于检测新增节点
    const prevNodeIdsRef = useRef<Set<string>>(new Set());

    // 初始化外部数据（批次模式）
    useEffect(() => {
        if (isExternalMode && externalData) {
            setGanttNodes(externalData.ganttNodes);

            // 如果提供了 timeBlocks 则使用，否则尝试生成
            if (externalData.timeBlocks) {
                setTimeBlocks(externalData.timeBlocks);
            } else {
                // 从外部 ganttNodes 生成 timeBlocks
                const blocks = generateTimeBlocks(externalData.ganttNodes, []);
                setTimeBlocks(blocks);
            }

            // 收集所有非操作节点的 ID（批次/阶段）
            const allExpandableKeys: string[] = [];
            const currentNodeIds = new Set<string>();
            const collectKeys = (nodes: GanttNode[]) => {
                nodes.forEach(node => {
                    currentNodeIds.add(node.id);
                    if (node.type !== 'operation') {
                        allExpandableKeys.push(node.id);
                    }
                    if (node.children) {
                        collectKeys(node.children);
                    }
                });
            };
            collectKeys(externalData.ganttNodes);

            // 检测是否有新节点
            const hasNewNodes = allExpandableKeys.some(id => !prevNodeIdsRef.current.has(id));
            const isFirstInit = prevNodeIdsRef.current.size === 0;

            if (isFirstInit || hasNewNodes) {
                // 首次初始化或有新节点时，合并到 expandedKeys
                setExpandedKeys(prev => {
                    const combined = new Set([...prev, ...allExpandableKeys]);
                    return Array.from(combined);
                });
            }

            // 更新上次节点ID集合
            prevNodeIdsRef.current = currentNodeIds;
        }
    }, [isExternalMode, externalData]);

    // 模板模式下自动加载数据
    useEffect(() => {
        if (!isExternalMode) {
            fetchData();
        }
    }, [fetchData, isExternalMode]);

    // 刷新数据方法
    const refreshData = useCallback(async () => {
        if (isExternalMode) {
            // 外部模式：触发外部刷新（由调用者处理）
            console.log('[useGanttData] External mode refresh requested');
            return;
        }
        return fetchData();
    }, [fetchData, isExternalMode]);

    return {
        stages,
        stageOperations,
        availableOperations,
        setAvailableOperations,
        ganttNodes,
        timeBlocks,
        expandedKeys,
        setExpandedKeys,
        loading,
        error,
        personnelCurve,
        refreshData,
        setGanttNodes,
        setTimeBlocks,
        isExternalMode
    };
};
